import { KeyValue } from '@angular/common';
import { Classifier } from '@nano/common/classifier';
import { ApiArrayResponse } from '@nano/core/api';
import { CardProductionStatus, CardProductType, CardStatus } from '@nano/card/common';

export enum AccountStatus {
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
  INITIATED = 'INITIATED'
}

export enum AccountOperationsBlock {
  DEBIT = 'DEBIT',
  CREDIT = 'CREDIT',
  ALL = 'ALL'
}

export interface Account extends IbanAccount {
  accountName: string;
  description?: string;
  isMain: boolean;
  branchBic?: string;
  correspondentBankName?: string;
  correspondentBankCode?: string;
  correspondentBankAddress?: string;
  availableFunds: number;
  reserved: number;
  hasDebitAvailableFunds: boolean;
  isViewOnly?: boolean;
  card?: CardAccount;
  operationsBlock?: AccountOperationsBlock;
  newCardAllowed?: boolean;
  permittedOperationCurrencies?: Map<PermittedOperationCurrencyType, Classifier[]>;
  paymentGatewayEligiblePayerCurrencies?: string[];
  hasPlaisBlock?: boolean;
  monthlyAvailableSpendingAmount?: string;
}

export enum PermittedOperationCurrencyType {
  CURRENCY_EXCHANGE = 'CURRENCY_EXCHANGE',
  OUTGOING_PAYMENTS = 'OUTGOING_PAYMENTS'
}

export const DEMO_ACCOUNT: Account = {
  iban: 'demo',
  currency: 'EUR',
  accountName: 'EUR',
  isMain: false,
  availableFunds: 0,
  reserved: 0,
  hasDebitAvailableFunds: false
};

export interface IbanAccount {
  iban: string;
  currency: string;
}

export interface CardAccount {
  status: CardStatus;
  productionStatus: CardProductionStatus;
  cardProductType: CardProductType;
  pan?: string;
  activationDate?: string;
  holderPartyId: string;
}

export const accountCompareFn = (a1: Account, a2: Account): boolean => (a1 && a2 ? a1.iban === a2.iban && a1.currency === a2.currency : a1 === a2);

export const isDemoAccount = (account: Account): boolean => account?.iban === DEMO_ACCOUNT.iban;

export const isEurAccount = (account: Account): boolean => account?.currency === 'EUR';

export const hasDebitAvailableFunds = (availableFunds: number, operationsBlock?: AccountOperationsBlock): boolean =>
  ([AccountOperationsBlock.ALL, AccountOperationsBlock.DEBIT].includes(operationsBlock) ? 0 : availableFunds) > 0;

export interface AccountInfo {
  availableFunds: KeyValue<string, number>;
  balances: KeyValue<string, number>;
  description: string;
  fundsBlocks: unknown[];
  holderPartyId: string;
  holds: KeyValue<string, number>;
  isMain: boolean;
  isViewOnly?: boolean;
  lastOrderedCard: CardAccount;
  number: string;
  openedOn: string;
  operationsBlock: AccountOperationsBlock;
  permittedOperationCurrencies?: KeyValue<string, number>;
  paymentGatewayEligiblePayerCurrencies?: string[];
  reservations: KeyValue<string, number>;
  status: AccountStatus;
  monthlyAvailableSpendingAmount: Amount;
}

export interface Amount {
  value: number;
  currency: string;
}

export interface AccountsResponse extends ApiArrayResponse<AccountInfo> {
  branchBic?: string;
  newCardAllowed?: boolean;
  selectedPartyHasAccounts?: boolean;
}
